import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

const DomainProfileView = ({ domain }) => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [customPromptText, setCustomPromptText] = useState("");
    const [tasksPerDay, setTasksPerDay] = useState(10);
    const [targetCrawlYear, setTargetCrawlYear] = useState("");
    const [backdateEnabled, setBackdateEnabled] = useState(false);
    const [isSavingPrompt, setIsSavingPrompt] = useState(false);

    useEffect(() => {
        if (domain && domain.id) {
            fetchProfile();
        }
    }, [domain]);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const data = await apiService.makeRequest(`/organic-seo/domains/${domain.id}/profile`);
            setProfile(data);
            setCustomPromptText(data.custom_prompt || "");
            setTasksPerDay(data.tasks_per_day === 0 ? 0 : (data.tasks_per_day || 10));
            setTargetCrawlYear(data.target_crawl_year || "");
            setBackdateEnabled(data.seo_parameters?.backdate_enabled || false);
        } catch (error) {
            setProfile(null);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateProfile = async () => {
        setIsGenerating(true);
        try {
            const data = await apiService.makeRequest(`/organic-seo/domains/${domain.id}/profile?description=`, { method: 'POST' });
            setProfile(data);
            toast.success("Style Identity Card generated!");
        } catch (error) {
            toast.error(error.message || "Failed to generate profile");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSaveCustomPrompt = async () => {
        setIsSavingPrompt(true);
        try {
            await apiService.makeRequest(`/organic-seo/domains/${domain.id}/profile`, {
                method: 'PUT',
                body: JSON.stringify({ 
                    custom_prompt: customPromptText,
                    tasks_per_day: tasksPerDay,
                    target_crawl_year: targetCrawlYear,
                    seo_parameters: {
                        ...(profile?.seo_parameters || {}),
                        backdate_enabled: backdateEnabled
                    }
                })
            });
            toast.success("Settings saved successfully!");
            // Refresh local profile
            setProfile({ 
                ...profile, 
                custom_prompt: customPromptText,
                tasks_per_day: tasksPerDay,
                target_crawl_year: targetCrawlYear,
                seo_parameters: {
                    ...(profile?.seo_parameters || {}),
                    backdate_enabled: backdateEnabled
                }
            });
        } catch (error) {
            toast.error(error.message || "Failed to save prompt");
        } finally {
            setIsSavingPrompt(false);
        }
    };

    return (
        <div className="domain-profile-view widget-card">
            <h3>Style Identity Card {domain ? <span style={{fontSize: "0.8em", fontWeight: "normal", color: "#666", marginLeft: "10px"}}>{domain.url.replace(/^https?:\/\//, '')}</span> : ""}</h3>
            {loading ? (
                <p>Loading profile...</p>
            ) : profile ? (
                <div className="profile-details">
                    <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                        <h4 className="text-gray-500 font-medium m-0">Identity Settings</h4>
                        <div className="flex gap-2">
                            <a 
                                href={`/admin/brain?tab=web-studio&domain=${encodeURIComponent(domain?.url)}`}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-bold shadow-sm transition flex items-center gap-2"
                                style={{ textDecoration: 'none' }}
                            >
                                🎨 Katifi Web Studio
                            </a>
                            <a 
                                href={`/admin/brain?tab=architect&domain=${encodeURIComponent(domain?.url)}`}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold shadow-sm transition flex items-center gap-2"
                                style={{ textDecoration: 'none' }}
                            >
                                🏗️ Launch Site Architect
                            </a>
                        </div>
                    </div>
                    <div className="profile-section">
                        <h4>Niche & Target Audience</h4>
                        <p>{profile.niche_target_audience}</p>
                    </div>
                    <div className="profile-section">
                        <h4>Tone of Voice</h4>
                        <p>{profile.tone_of_voice}</p>
                    </div>
                    <div className="profile-section">
                        <h4>Forbidden Topics & Words</h4>
                        <p>{profile.forbidden_topics_words}</p>
                    </div>

                    <div className="profile-section mt-4 custom-prompt-section">
                        <h4>Custom Rewrite Prompt (Optional)</h4>
                        <p className="description-text mb-2 text-sm text-gray-400">Add any explicit instructions the Agent must obey when rewriting pages for this site.</p>
                        <textarea
                            className="form-control"
                            rows="2"
                            placeholder="e.g. Always end articles with a call-to-action to contact your firm for a free consultation."
                            value={customPromptText}
                            onChange={(e) => setCustomPromptText(e.target.value)}
                        />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-700">
                        <div className="profile-section">
                            <h4>Target Crawl Year (Filter)</h4>
                            <p className="text-sm text-gray-400 mb-2">Only crawl posts/pages from this year (e.g. '2023'). Leave blank to crawl everything.</p>
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder="e.g. 2023"
                                value={targetCrawlYear}
                                onChange={(e) => setTargetCrawlYear(e.target.value)}
                            />
                        </div>
                        <div className="profile-section">
                            <h4>Tasks Per Day (Drip-Feed Limit)</h4>
                            <p className="text-sm text-gray-400 mb-2">Maximum number of AI rewrite tasks to execute daily. Recommended: 5-10.</p>
                            <input 
                                type="number" 
                                className="form-control" 
                                min="1"
                                value={tasksPerDay}
                                onChange={(e) => setTasksPerDay(parseInt(e.target.value) || 10)}
                            />
                        </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="form-check flex items-center gap-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <input 
                                type="checkbox" 
                                className="form-check-input w-5 h-5 cursor-pointer accent-blue-600" 
                                id="backdateToggle"
                                checked={backdateEnabled}
                                onChange={(e) => setBackdateEnabled(e.target.checked)}
                            />
                            <label className="form-check-label cursor-pointer select-none text-gray-800" htmlFor="backdateToggle">
                                <strong>Simulate Historical Publishing (Backdate 1-2.5 years)</strong>
                                <br/>
                                <span className="text-sm text-gray-500">If unchecked, standard +1 hour Future Drip-Feed is used instead.</span>
                            </label>
                        </div>
                    </div>

                    <div className="mt-6">
                        <button className="btn btn-primary flex items-center gap-2" onClick={handleSaveCustomPrompt} disabled={isSavingPrompt}>
                            {isSavingPrompt ? "Saving Settings..." : "Save Settings"} 
                        </button>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-700">
                        <button className="btn btn-secondary" onClick={handleGenerateProfile} disabled={isGenerating}>
                            {isGenerating ? "Regenerating..." : "Regenerate Profile Base"}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <p>No Style Identity Card exists for this domain.</p>
                    <button className="btn btn-primary" onClick={handleGenerateProfile} disabled={isGenerating}>
                        {isGenerating ? "Analyzing Domain..." : "Generate with Brand Strategist Agent"}
                    </button>
                    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <a 
                            href={`/admin/brain?tab=web-studio&domain=${encodeURIComponent(domain?.url)}`}
                            className="btn" 
                            style={{ backgroundColor: '#3b82f6', color: 'white', display: 'inline-block', textDecoration: 'none' }}
                        >
                            🎨 Katifi Web Studio
                        </a>
                        <a 
                            href={`/admin/brain?tab=architect&domain=${encodeURIComponent(domain?.url)}`}
                            className="btn" 
                            style={{ backgroundColor: '#10b981', color: 'white', display: 'inline-block', textDecoration: 'none' }}
                        >
                            🏗️ Launch Site Architect
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DomainProfileView;
